import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import { 
  ArrowLeft, Plus, X, Trophy, CheckSquare, Clock, 
  Target, Heart, Lightbulb, Zap, Battery, TrendingUp, Save
} from "lucide-react";

// ListSection component - MOVED OUTSIDE to prevent re-mounting on parent state changes
const ListSection = ({ title, icon: Icon, items, field, newValue, onNewValueChange, onAddItem, onRemoveItem, color }) => {
  return (
    <Card className={`border-l-4 ${color}`}>
      <CardHeader className="py-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="w-4 h-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {items && items.length > 0 && (
          <div className="space-y-2">
            {items.map((item, index) => (
              <div key={index} className="flex items-center justify-between bg-neutral-50 px-3 py-2 rounded-lg">
                <span className="text-sm">{item}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveItem(index)}
                  className="h-7 w-7 p-0 text-neutral-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center space-x-2">
          <Input
            placeholder="Type and press Enter or click +"
            value={newValue}
            onChange={(e) => onNewValueChange(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && newValue.trim()) {
                onAddItem();
              }
            }}
            className="h-9 text-sm flex-1"
            data-testid={`${field}-input`}
          />
          <Button 
            size="sm" 
            onClick={onAddItem}
            disabled={!newValue.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 h-9 px-3 flex-shrink-0"
            data-testid={`${field}-add-btn`}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const WeeklyLogPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - today.getDay() + 1);
  const weekStart = monday.toISOString().split("T")[0];

  const [form, setForm] = useState({
    week_start: weekStart,
    accomplishments: [],
    completed_tasks: [],
    active_tasks: [],
    upcoming_tasks: [],
    next_week_goals: [],
    self_care_activities: [],
    moments_of_clarity: [],
    focus_rating: 5,
    energy_rating: 5,
    momentum_rating: 5,
    notes: ""
  });

  const [newItems, setNewItems] = useState({
    accomplishment: "",
    completed_task: "",
    active_task: "",
    upcoming_task: "",
    next_week_goal: "",
    self_care: "",
    clarity: ""
  });

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await authFetch(`${API}/weekly-logs`, {  });
      if (response.ok) {
        setLogs(await response.json());
      }
    } catch (_error) {
      console.error("Error fetching logs:", _error);
    }
  };

  const addItem = (field, value, newItemKey) => {
    if (value.trim()) {
      setForm({ ...form, [field]: [...form[field], value.trim()] });
      setNewItems({ ...newItems, [newItemKey]: "" });
    }
  };

  const removeItem = (field, index) => {
    setForm({ ...form, [field]: form[field].filter((_, i) => i !== index) });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/weekly-logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success("Weekly log saved successfully!");
        setShowForm(false);
        fetchLogs();
        // Reset form
        setForm({
          ...form,
          accomplishments: [],
          completed_tasks: [],
          active_tasks: [],
          upcoming_tasks: [],
          next_week_goals: [],
          self_care_activities: [],
          moments_of_clarity: [],
          focus_rating: 5,
          energy_rating: 5,
          momentum_rating: 5,
          notes: ""
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save log");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="weekly-log-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate("/dashboard")}
                className="text-neutral-600"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <h1 className="text-xl font-semibold text-neutral-900">Weekly Progress</h1>
            </div>
            {!showForm && (
              <Button 
                onClick={() => setShowForm(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="new-log-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Log
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {showForm ? (
          <div className="space-y-6 animate-fade-in-up" data-testid="weekly-log-form">
            {/* Week Selection */}
            <Card className="border-neutral-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-neutral-700">Week Starting</Label>
                    <Input
                      type="date"
                      value={form.week_start}
                      onChange={(e) => setForm({ ...form, week_start: e.target.value })}
                      className="w-48 mt-1"
                      data-testid="week-start-input"
                    />
                  </div>
                  <Button 
                    variant="ghost" 
                    onClick={() => setShowForm(false)}
                    className="text-neutral-500"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Lists Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ListSection
                title="Accomplishments"
                icon={Trophy}
                items={form.accomplishments}
                field="accomplishments"
                newValue={newItems.accomplishment || ""}
                onNewValueChange={(val) => setNewItems(prev => ({ ...prev, accomplishment: val }))}
                onAddItem={() => {
                  if (newItems.accomplishment?.trim()) {
                    addItem("accomplishments", newItems.accomplishment, "accomplishment");
                  }
                }}
                onRemoveItem={(index) => removeItem("accomplishments", index)}
                color="border-l-amber-500"
              />
              <ListSection
                title="Completed Tasks"
                icon={CheckSquare}
                items={form.completed_tasks}
                field="completed_tasks"
                newValue={newItems.completed_task || ""}
                onNewValueChange={(val) => setNewItems(prev => ({ ...prev, completed_task: val }))}
                onAddItem={() => {
                  if (newItems.completed_task?.trim()) {
                    addItem("completed_tasks", newItems.completed_task, "completed_task");
                  }
                }}
                onRemoveItem={(index) => removeItem("completed_tasks", index)}
                color="border-l-green-500"
              />
              <ListSection
                title="Active Tasks"
                icon={Clock}
                items={form.active_tasks}
                field="active_tasks"
                newValue={newItems.active_task || ""}
                onNewValueChange={(val) => setNewItems(prev => ({ ...prev, active_task: val }))}
                onAddItem={() => {
                  if (newItems.active_task?.trim()) {
                    addItem("active_tasks", newItems.active_task, "active_task");
                  }
                }}
                onRemoveItem={(index) => removeItem("active_tasks", index)}
                color="border-l-blue-500"
              />
              <ListSection
                title="Upcoming Tasks"
                icon={Target}
                items={form.upcoming_tasks}
                field="upcoming_tasks"
                newValue={newItems.upcoming_task || ""}
                onNewValueChange={(val) => setNewItems(prev => ({ ...prev, upcoming_task: val }))}
                onAddItem={() => {
                  if (newItems.upcoming_task?.trim()) {
                    addItem("upcoming_tasks", newItems.upcoming_task, "upcoming_task");
                  }
                }}
                onRemoveItem={(index) => removeItem("upcoming_tasks", index)}
                color="border-l-purple-500"
              />
              <ListSection
                title="Next Week Goals"
                icon={TrendingUp}
                items={form.next_week_goals}
                field="next_week_goals"
                newValue={newItems.next_week_goal || ""}
                onNewValueChange={(val) => setNewItems(prev => ({ ...prev, next_week_goal: val }))}
                onAddItem={() => {
                  if (newItems.next_week_goal?.trim()) {
                    addItem("next_week_goals", newItems.next_week_goal, "next_week_goal");
                  }
                }}
                onRemoveItem={(index) => removeItem("next_week_goals", index)}
                color="border-l-emerald-500"
              />
              <ListSection
                title="Self-Care Activities"
                icon={Heart}
                items={form.self_care_activities}
                field="self_care_activities"
                newValue={newItems.self_care || ""}
                onNewValueChange={(val) => setNewItems(prev => ({ ...prev, self_care: val }))}
                onAddItem={() => {
                  if (newItems.self_care?.trim()) {
                    addItem("self_care_activities", newItems.self_care, "self_care");
                  }
                }}
                onRemoveItem={(index) => removeItem("self_care_activities", index)}
                color="border-l-pink-500"
              />
            </div>

            {/* Moments of Clarity */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center">
                  <Lightbulb className="w-5 h-5 mr-2 text-yellow-500" />
                  Moments of Clarity / Breakthroughs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-3">
                  {form.moments_of_clarity.map((item, index) => (
                    <div key={index} className="flex items-start justify-between bg-yellow-50 px-3 py-2 rounded-lg border border-yellow-100">
                      <span className="text-sm text-neutral-700">{item}</span>
                      <button 
                        onClick={() => removeItem("moments_of_clarity", index)}
                        className="text-neutral-400 hover:text-red-500 transition-colors ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center space-x-2">
                  <Textarea
                    placeholder="Describe a breakthrough or moment of clarity..."
                    value={newItems.clarity}
                    onChange={(e) => setNewItems({ ...newItems, clarity: e.target.value })}
                    className="min-h-20 text-sm"
                    data-testid="clarity-input"
                  />
                  <Button 
                    onClick={() => addItem("moments_of_clarity", newItems.clarity, "clarity")}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Energy Ratings */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Rate Your Week</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <Zap className="w-5 h-5 mr-2 text-amber-500" />
                      <span className="font-medium text-neutral-700">Focus</span>
                    </div>
                    <span className="font-mono text-lg text-neutral-900">{form.focus_rating}/10</span>
                  </div>
                  <Slider
                    value={[form.focus_rating]}
                    onValueChange={([value]) => setForm({ ...form, focus_rating: value })}
                    max={10}
                    step={1}
                    className="py-2"
                    data-testid="focus-slider"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <Battery className="w-5 h-5 mr-2 text-green-500" />
                      <span className="font-medium text-neutral-700">Energy</span>
                    </div>
                    <span className="font-mono text-lg text-neutral-900">{form.energy_rating}/10</span>
                  </div>
                  <Slider
                    value={[form.energy_rating]}
                    onValueChange={([value]) => setForm({ ...form, energy_rating: value })}
                    max={10}
                    step={1}
                    className="py-2"
                    data-testid="energy-slider"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2 text-blue-500" />
                      <span className="font-medium text-neutral-700">Momentum</span>
                    </div>
                    <span className="font-mono text-lg text-neutral-900">{form.momentum_rating}/10</span>
                  </div>
                  <Slider
                    value={[form.momentum_rating]}
                    onValueChange={([value]) => setForm({ ...form, momentum_rating: value })}
                    max={10}
                    step={1}
                    className="py-2"
                    data-testid="momentum-slider"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Additional Notes */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Additional Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Any other thoughts, reflections, or notes for this week..."
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="min-h-32"
                  data-testid="notes-input"
                />
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex justify-end">
              <Button 
                onClick={handleSubmit}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700 h-12 px-8"
                data-testid="save-log-btn"
              >
                {loading ? "Saving..." : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Weekly Log
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* Logs List */
          <div className="space-y-4">
            {logs.length === 0 ? (
              <Card className="border-neutral-200">
                <CardContent className="py-12 text-center">
                  <Trophy className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">No logs yet</h3>
                  <p className="text-neutral-500 mb-4">Start tracking your weekly progress</p>
                  <Button 
                    onClick={() => setShowForm(true)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Log
                  </Button>
                </CardContent>
              </Card>
            ) : (
              logs.map((log) => (
                <Card key={log.log_id} className="border-neutral-200 hover:border-emerald-200 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-neutral-900 mb-1">Week of {log.week_start}</h3>
                        <p className="text-sm text-neutral-500">
                          Logged on {new Date(log.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center text-amber-600">
                          <Zap className="w-4 h-4 mr-1" />
                          {log.focus_rating}/10
                        </div>
                        <div className="flex items-center text-green-600">
                          <Battery className="w-4 h-4 mr-1" />
                          {log.energy_rating}/10
                        </div>
                        <div className="flex items-center text-blue-600">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          {log.momentum_rating}/10
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-amber-50 rounded-lg p-3">
                        <p className="text-xs text-amber-700 mb-1">Accomplishments</p>
                        <p className="text-2xl font-semibold text-amber-900">{log.accomplishments?.length || 0}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3">
                        <p className="text-xs text-green-700 mb-1">Tasks Completed</p>
                        <p className="text-2xl font-semibold text-green-900">{log.completed_tasks?.length || 0}</p>
                      </div>
                      <div className="bg-pink-50 rounded-lg p-3">
                        <p className="text-xs text-pink-700 mb-1">Self-Care</p>
                        <p className="text-2xl font-semibold text-pink-900">{log.self_care_activities?.length || 0}</p>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-3">
                        <p className="text-xs text-yellow-700 mb-1">Clarity Moments</p>
                        <p className="text-2xl font-semibold text-yellow-900">{log.moments_of_clarity?.length || 0}</p>
                      </div>
                    </div>

                    {log.notes && (
                      <div className="mt-4 pt-4 border-t border-neutral-100">
                        <p className="text-sm text-neutral-600">{log.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyLogPage;
